import { describeSdkError, isTransferCancelled } from "@istari-frontend/shared";
import { useEffect, useId, useRef, useState } from "react";

import {
  cancelUpload,
  canResumeUpload,
  prepareUploadResume,
  releasePreparedUploadResume,
  subscribeUpload,
  subscribeUploadEligibility,
  type UploadSnapshot,
} from "~/resource/data-source/upload-payloads";

import {
  assertUploadSessionCurrent,
  awaitUploadSessionReady,
  captureUploadSession,
  isUploadSessionCurrent,
} from "../data-source/upload-session-callbacks";

export function UploadOperationControls({
  operationId,
  retry,
  snapshot: retainedSnapshot,
}: {
  operationId: string;
  retry?: () => Promise<unknown>;
  snapshot?: UploadSnapshot;
}) {
  const [observedSnapshot, setSnapshot] = useState<UploadSnapshot>({ state: "pending" });
  const snapshot = retainedSnapshot ?? observedSnapshot;
  const [eligible, setEligible] = useState(() => canResumeUpload(operationId));
  useEffect(
    () => subscribeUploadEligibility(operationId, setEligible),
    [operationId, snapshot.state, snapshot.canResume],
  );
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const id = useId();
  const unsubscribe = useRef<(() => void) | undefined>(undefined);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    unsubscribe.current = subscribeUpload(operationId, setSnapshot);
    return () => {
      mounted.current = false;
      unsubscribe.current?.();
    };
  }, [operationId]);
  const resume = async (file?: File) => {
    if (verifying || !retry) return;
    let session: ReturnType<typeof captureUploadSession>;
    try {
      session = captureUploadSession();
      assertUploadSessionCurrent(session);
    } catch {
      return;
    }
    setVerifying(true);
    setError("");
    try {
      await prepareUploadResume(operationId, file);
      await awaitUploadSessionReady(session);
      unsubscribe.current?.();
      unsubscribe.current = subscribeUpload(operationId, setSnapshot);
    } catch (error) {
      releasePreparedUploadResume(operationId);
      if (mounted.current && isUploadSessionCurrent(session) && !isTransferCancelled(error)) {
        setError("Unable to resume upload. Choose the original file and try again.");
      }
      if (mounted.current) setVerifying(false);
      return;
    }
    try {
      await retry();
      if (releasePreparedUploadResume(operationId)) throw new Error("Upload did not start");
    } catch (error) {
      releasePreparedUploadResume(operationId);
      if (!isTransferCancelled(error) && isUploadSessionCurrent(session)) {
        const message = await describeSdkError(error, "Upload could not finish. Try again.");
        if (mounted.current && isUploadSessionCurrent(session)) setError(message);
      }
    } finally {
      if (mounted.current) {
        setEligible(canResumeUpload(operationId));
        setVerifying(false);
      }
    }
  };
  const active = snapshot.state === "pending" || snapshot.state === "uploading";
  const percent = snapshot.bytesTotal
    ? Math.min(100, Math.floor(((snapshot.bytesDone ?? 0) * 100) / snapshot.bytesTotal))
    : undefined;
  return (
    <div
      className="text-muted-foreground flex flex-wrap items-center gap-1 text-xs"
      onClick={(event) => event.stopPropagation()}
    >
      <span role="status" aria-live="polite" className="tabular-nums">
        {uploadStatus(snapshot)}
        {active && percent !== undefined && snapshot.phase !== "metadata" && snapshot.phase !== "finalizing"
          ? ` ${percent}%`
          : ""}
      </span>
      {active && snapshot.phase !== "metadata" && (
        <button
          className="hover:bg-accent min-h-10 rounded px-2"
          type="button"
          onClick={() => cancelUpload(operationId)}
        >
          Cancel upload
        </button>
      )}
      {snapshot.canResume && eligible && retry && (
        <button
          className="hover:bg-accent min-h-10 rounded px-2"
          type="button"
          disabled={verifying}
          onClick={() => input.current?.click()}
        >
          {verifying ? "Verifying file" : "Resume upload"}
        </button>
      )}
      <input
        ref={input}
        id={id}
        aria-label="Choose original file to resume upload"
        className="sr-only"
        type="file"
        onChange={(event) => {
          const file = event.target.files?.[0];
          const target = event.target;
          target.value = "";
          if (file) void resume(file);
        }}
      />
      {error && (
        <span role="alert" className="text-destructive">
          {error}
        </span>
      )}
    </div>
  );
}

function uploadStatus(snapshot: UploadSnapshot): string {
  if (snapshot.state === "uploading") {
    if (snapshot.phase === "hash") return "Hashing";
    if (snapshot.phase === "finalizing") return "Finalizing upload";
    if (snapshot.phase === "metadata") return "Saving metadata";
  }
  const labels = {
    pending: "Queued",
    cancelled: "Cancelled",
    error: "Failed",
    success: "Complete",
    uploading: "Uploading",
  };
  return labels[snapshot.state];
}
